import { Lock } from "@mui/icons-material";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, Paper, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { ChangeEvent, FC, useState } from "react";
import InputMask from 'react-input-mask';
import { modalNewAdmission } from "../../../atoms/atom";
import { getLocation } from "../../../pages/audit-admission/AuditAdmission";
import { AuditService } from "../../../services/AuditService";
import { useUtils } from "../../../utils/utilFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";

const NewAdmissionModal: FC<{id: number}> = (props: {id: number}) => {
	const [open, setOpen] = useAtom(modalNewAdmission);
	const [cpf, setCpf] = useState('');
	const [expiration, setExpiration] = useState('24');
	const [id, setId] = useState(0);
	const [loading, setLoading] = useState(false);
	const { validateCPF, handleCopy } = useUtils();
	const service = new AuditService();
	const { showError, showAlert } = useAlert();
	const invalidCpf = !!cpf && !cpf.includes("_") && !validateCPF(cpf);
	const link = 'https://interno.inovação.seg.br/auditoria/nova-admissao';

	return (
		<Dialog
			open={open}
			onClose={() => {
				setOpen(false);
				setCpf('');
				setId(0);
			}}
		>
			<DialogTitle>Nova admissão</DialogTitle>
			<DialogContent id="new-admission-dialog">
				<Box
					sx={{
						width: { xs: '100%', sm: '400px' },
						height: { xs: '350px', sm: "280px"},
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						gap: 2
					}}
				>
					{!!id
						? <>
							<Typography sx={{ alignSelf: 'center' }} variant="h6">Link gerado com sucesso!</Typography>
							<Button
								fullWidth
								sx={{ width: "240px", alignSelf: 'center' }}
								size="small"
								variant="contained"
								color="success"
								onClick={() => handleCopy(`${link}/${id}`)}
							>
								Copiar link
							</Button>
						</>
						: <>
							<InputMask mask="999.999.999-99"
								value={cpf}
								onChange={(e: ChangeEvent<HTMLInputElement>) => setCpf(e.target.value)}
							>
								{(inputProps: any) => (
									<TextField
										error={invalidCpf}
										helperText={invalidCpf ? "CPF inválido" : "Será gerado um link único e seguro para o candidato preencher seus dados e enviar os documentos"}
										size="small"
										{...inputProps}
										label={"Digite o CPF do funcionário"}
										variant="outlined"
										fullWidth
									/>
								)}
							</InputMask>
							<FormControl component="fieldset">
								<FormLabel component="legend">Expira em</FormLabel>
								<RadioGroup
									row
									name="expiration"
									value={expiration}
									onChange={(e) => setExpiration(e.target.value)}
								>
									<FormControlLabel value="24" control={<Radio size="small" />} label="24 horas" />
									<FormControlLabel value="48" control={<Radio size="small" />} label="48 horas" />
									<FormControlLabel value="168" control={<Radio size="small" />} label="7 dias" />
									<FormControlLabel value="never" control={<Radio size="small" />} label="Nunca" />
								</RadioGroup>
							</FormControl>
							<Paper elevation={1} sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
								<Lock color="primary" />
								<Typography variant="caption">
									O link será automaticamente desativado após o prazo escolhido. Após expirar, será necessário gerar um novo link
								</Typography>
							</Paper>
						</>
					}
				</Box>
			</DialogContent>
			{!id
				? <DialogActions>
					<Button
						disabled={!cpf || cpf.includes("_") || invalidCpf || loading}
						color="success"
						variant="outlined"
						onClick={create}
					>
						{loading
							? <CircularProgress sx={{color: "rgb(56, 78, 243)"}} size={'25px'}/>
							: "Gerar link seguro"}
					</Button>
				</DialogActions>
				: <></>
			}
		</Dialog>
	);

	async function create() {
		setLoading(true);
		let expires_at: number | null = null;
		if (expiration !== 'never') {
			const now = new Date();
			now.setHours(now.getHours() + parseInt(expiration, 10));
			expires_at = now.getTime();
		}
		const { latitude, longitude, locationName } = await getLocation();
		const logPayload = {
			cpf,
			groupId: props.id,
			expires_at,
			latitude,
			longitude,
			locationName,
			expiresAt: expires_at,
		}
		const response = await service.createAdmission(logPayload);
		if (response && response.data && response.data.id_funcionario) {
			setId(response.data.id_funcionario);
			handleCopy(`${link}/${response.data.id_funcionario}`)
			showAlert("Link gerado e copiado com sucesso!");
		} else {
			if (!!response.data.error) {
				showError(response.data.error);
			}
		}
		setLoading(false);
	}
};

export default NewAdmissionModal;